import { format, parse } from "date-fns";
import {
    BASIC_TIME_FORMAT,
    DATE_MONTH_YEAR_FORMAT_DASH,
    DAY_DATE_MONTH_YEAR_FORMAT,
    LONG_TIME_FORMAT,
    SHORT_DAY_DATE_MONTH_YEAR,
} from "repoV2/features/Common/modules/DateTime/modules/Time/Time.constants";
import { IWebinarSession } from "../modules/PaymentDetailsSection/modules/FormGroups/modules/SessionDetailsFormGroup/modules/WebinarSessions/WebinarSession.interfaces";

/**
 * @dev
 * Option used for Multiple Sessions Selector Dropdown
 */
type ISessionOption = {
    id: string; // unique id for session option
    label: string; // label for session option
    selectedLabel: string; // render value for session option shown in dropdown input upon selection
    value: string; // actual value for session option shown in dropdown input upon selection
};

/**
 * @dev
 * Options map used for Multiple Sessions Selector Dropdown
 * where key is equal to `ISessionOption["value"]` and value is equal to session option data
 */
type ISessionsObjectMap = {
    [key: ISessionOption["value"]]: ISessionOption;
};

/**
 * Converts the date format to Wed, 12 Jun '24
 * @param dateValue date string, in the format Wednesday, 12 Jun '24
 * @returns returns the date string in the format Wed, 12 Jun '24
 */
const getSessionDateValue = (dateValue: string) => {
    try {
        const parsedDate = parse(
            dateValue,
            DAY_DATE_MONTH_YEAR_FORMAT,
            new Date()
        );
        return format(parsedDate, SHORT_DAY_DATE_MONTH_YEAR);
    } catch (err) {
        return "";
    }
};

/**
 * Converts the date value in the format 2024-06-12
 * @param dateValue date string in the format Wednesday, 12 Jun '24
 * @returns returns the date string in the format 2024-06-12
 */
export const getSlotDateValue = (dateValue: string) => {
    try {
        const parsedDate = parse(
            dateValue,
            DAY_DATE_MONTH_YEAR_FORMAT,
            new Date()
        );
        return format(parsedDate, DATE_MONTH_YEAR_FORMAT_DASH);
    } catch (err) {
        return "";
    }
};

/**
 * Converts the time value in the twenty four hour format
 * @param timeValue in the format 11:05 PM
 * @returns the time value in the format 23:05
 */
export const getSessionTimeValue = (timeValue: string) => {
    try {
        const parsedTime = parse(timeValue, BASIC_TIME_FORMAT, new Date());
        return format(parsedTime, LONG_TIME_FORMAT);
    } catch {
        return "";
    }
};
/**
 * @description This is used to get the session Details options array and its Object map
 * @param arg => { sessions }
 * sessions => session details List
 * @returns `{sessionOptions, sessionsObjectMap}`
 * sessionOptions => array of session options,
 * sessionsObjectMap => object map for sessions
 */
export default function getSessionDetailsOptionsList({
    sessions,
}: {
    sessions: IWebinarSession[];
}) {
    const sessionOptions: ISessionOption[] = [];
    const sessionsObjectMap: ISessionsObjectMap = {};

    sessions.forEach(({ uuid, startDate, startTime, endTime }) => {
        const labelValue = `${getSessionDateValue(
            startDate
        )} | ${getSessionTimeValue(startTime)} - ${getSessionTimeValue(
            endTime
        )}`;
        const option = {
            id: uuid,
            label: labelValue,
            selectedLabel: labelValue,
            value: uuid,
        };

        sessionOptions.push(option);
        sessionsObjectMap[option.value] = option;
    });

    return {
        sessionOptions,
        sessionsObjectMap,
    };
}
